import React from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const useStyles = createUseStyles({
  tabBar: {
    display: 'flex',
    background: '#1a0f0a',
    borderTop: '1px solid #4a3020',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    zIndex: 10,
    position: 'relative',
  },
  tab: {
    flex: 1,
    minHeight: '56px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '8px 0 7px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderTop: '2px solid transparent',
    fontFamily: 'inherit',
    transition: 'color .12s ease, background .12s ease',
    '&:active': {
      background: 'rgba(255,255,255,0.04)',
    },
  },
  tabActive: {
    borderTopColor: '#d94f2b',
    background: 'rgba(217,79,43,0.08)',
  },
  tabLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  tabLabel: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  badge: {
    fontFamily: MONO,
    background: '#3a2718',
    color: '#e8d6ae',
    fontSize: '9.5px',
    fontWeight: 700,
    borderRadius: '7px',
    padding: '1px 6px',
    lineHeight: 1.5,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
  },
  // Players carries a count; events carried nothing, so a round's worth of log
  // activity was invisible until you thought to look.
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: '#e0a325',
    boxShadow: '0 0 0 2px rgba(224,163,37,0.18)',
  },
});

/** Simple geometric marks instead of emoji — they inherit currentColor. */
function TabMark({ id }) {
  if (id === 'players') {
    return (
      <svg width="19" height="19" viewBox="0 0 20 20" fill="currentColor">
        <circle cx="7" cy="6.4" r="3.2" />
        <circle cx="14.4" cy="7.4" r="2.4" opacity="0.7" />
        <path d="M1.6 16.4c0-3 2.4-4.8 5.4-4.8s5.4 1.8 5.4 4.8z" />
        <path d="M13.4 11.9c2.6.2 5 1.8 5 4.5h-4.2c0-1.7-.4-3.2-.8-4.5z" opacity="0.7" />
      </svg>
    );
  }
  if (id === 'events') {
    return (
      <svg width="19" height="19" viewBox="0 0 20 20" fill="currentColor">
        <rect x="2" y="4" width="3" height="2.4" rx="1.2" />
        <rect x="7" y="4" width="11" height="2.4" rx="1.2" opacity="0.75" />
        <rect x="2" y="8.8" width="3" height="2.4" rx="1.2" />
        <rect x="7" y="8.8" width="11" height="2.4" rx="1.2" opacity="0.75" />
        <rect x="2" y="13.6" width="3" height="2.4" rx="1.2" />
        <rect x="7" y="13.6" width="11" height="2.4" rx="1.2" opacity="0.75" />
      </svg>
    );
  }
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="currentColor">
      <rect x="2" y="2" width="7" height="7" rx="1.4" />
      <rect x="11" y="2" width="7" height="7" rx="1.4" opacity="0.65" />
      <rect x="2" y="11" width="7" height="7" rx="1.4" opacity="0.65" />
      <rect x="11" y="11" width="7" height="7" rx="1.4" />
    </svg>
  );
}

const TABS = [
  { id: 'game', labelKey: 'game.tabGame' },
  { id: 'players', labelKey: 'game.tabPlayers' },
  { id: 'events', labelKey: 'game.tabEvents' },
];

function MobileTabs({ activeTab, onTabChange, playerCount, unreadEvents }) {
  const classes = useStyles();
  const t = useT();

  return (
    <div className={classes.tabBar}>
      {TABS.map(tab => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
            onClick={() => onTabChange(tab.id)}
            style={{ color: isActive ? '#e35c36' : '#8a7259' }}
          >
            <TabMark id={tab.id} />
            <span className={classes.tabLabelRow}>
              <span className={classes.tabLabel}>{t(tab.labelKey)}</span>
              {tab.id === 'players' && playerCount > 0 && (
                <span className={classes.badge}>{playerCount}</span>
              )}
              {tab.id === 'events' && unreadEvents && !isActive && (
                <span className={classes.dot} role="status" aria-label={t('log.unread')} />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default MobileTabs;
